import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Select, Table } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  LabResultVerbiage,
  useLabResultVerbiages,
} from '../../store/lab-result-verbiages';

/**
 * LabResultVerbiages props
 */
interface Props {}

type Inputs = {
  fromResult?: string;
  result?: string;
  value?: string;
  name?: string;
};

/**
 * LabResultVerbiages Component
 */
const LabResultVerbiages: FunctionComponent<Props> = ({ ...props }) => {
  const {
    fetchLabResultVerbiage,
    deleteLabResultVerbiage,
    updating,
    updateLabResultVerbiage,
    list,
  } = useLabResultVerbiages();
  const [selectedName, setSelectedname] = useState<string>();
  const [updateData, setUpdateData] = useState<LabResultVerbiage>();

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = data => save(data);

  const names = [
    'LDL-C',
    'Triglycerides',
    'HDL-C (Male)',
    'HDL-C (Female)',
    'Lp(a)',
    'hsCRP',
    'Fasting Glucose',
    'HbA1c',
    '*',
  ];

  const resultOptions = [
    'TNP',
    '<DL',
    'Optimal',
    'Borderline',
    'Increased Risk',
    'Panic Value',
    '>UL',
  ];

  const resultPreview = useCallback((result: string): string => {
    switch (result) {
      case 'TNP':
        return 'Not Reportable';
      case '<DL':
        return 'Below Detection Limit';
      case '>UL':
        return 'Above Detection Limit';
      default:
        return result;
    }
  }, []);

  const columns = [
    {
      title: 'From Result',
      dataIndex: 'fromResult',
      key: 'fromResult',
      render: (value: string) => resultPreview(value || '--'),
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      render: (value: string) => resultPreview(value || '--'),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: string) => (
        <div className="truncate max-w-[350px]">{value}</div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_item: any, data: LabResultVerbiage) => (
        <div className="gap-2 flex">
          <button
            onClick={() => {
              setUpdateData(data);
              // setValueMsg(data.value);
            }}
            type="button">
            Update
          </button>
          <button
            className="text-red"
            onClick={async () => {
              if (data?.id) {
                await deleteLabResultVerbiage({ id: data.id });
                fetchLabResultVerbiage({ name: selectedName });
              }
            }}
            type="button">
            Delete
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    setValue('fromResult', updateData?.fromResult);
    setValue('result', updateData?.result);
    setValue('value', updateData?.value);
    setValue('name', updateData?.name);
  }, [setValue, updateData]);

  useEffect(() => {
    fetchLabResultVerbiage({ name: selectedName });
  }, [fetchLabResultVerbiage, selectedName]);

  const save = useCallback(
    async (data: Inputs) => {
      if (updateData) {
        await updateLabResultVerbiage({ ...updateData, ...data });
        await fetchLabResultVerbiage({ name: selectedName });
        setUpdateData(undefined);
      }
    },
    [fetchLabResultVerbiage, selectedName, updateData, updateLabResultVerbiage],
  );

  return (
    <div className="p-2">
      <div className="flex flex-row justify-between items-center">
        <Select
          className="min-w-[200px]"
          placeholder="Select name to filter"
          options={[
            { label: 'All', value: '' },
            ...names.map(e => ({ value: e, label: e })),
          ]}
          onChange={value => setSelectedname(value)}
        />
        <button
          className="bg-red p-2 text-white rounded"
          type="button"
          onClick={() => setUpdateData({} as LabResultVerbiage)}>
          Create New
        </button>
      </div>
      <Table dataSource={list?.data || []} columns={columns} />
      <Modal
        title="Update"
        footer={null}
        open={Boolean(updateData)}
        confirmLoading={
          updateData && updateData?.id ? updating[updateData?.id] : false
        }
        onCancel={() => setUpdateData(undefined)}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-1 gap-2 flex-col md:flex-row ">
              <Controller
                name="fromResult"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <Form.Item
                    help={errors.fromResult?.message}
                    validateStatus={
                      errors.fromResult?.message ? 'error' : undefined
                    }>
                    <Select
                      className="min-w-[200px] flex-1"
                      placeholder="From Result"
                      options={resultOptions.map(e => ({
                        value: e,
                        label: resultPreview(e),
                      }))}
                      {...field}
                    />
                  </Form.Item>
                )}
              />

              <Controller
                name="result"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'This field is required.',
                  },
                }}
                render={({ field }) => (
                  <Form.Item
                    help={errors.result?.message}
                    validateStatus={
                      errors.result?.message ? 'error' : undefined
                    }>
                    <Select
                      className="min-w-[200px] flex-1"
                      placeholder="Result"
                      options={resultOptions.map(e => ({
                        value: e,
                        label: resultPreview(e),
                      }))}
                      {...field}
                    />
                  </Form.Item>
                )}
              />
            </div>
            {!updateData?.id ? (
              <Controller
                name="name"
                control={control}
                rules={{
                  required: { value: true, message: 'This field is required.' },
                }}
                render={({ field }) => (
                  <Form.Item
                    help={errors.name?.message}
                    validateStatus={errors.name?.message ? 'error' : undefined}>
                    <Select
                      className="min-w-[200px]"
                      placeholder="Name"
                      options={[
                        { label: 'All', value: '' },
                        ...names.map(e => ({ value: e, label: e })),
                      ]}
                      {...field}
                    />
                  </Form.Item>
                )}
              />
            ) : null}
            <Controller
              name="value"
              control={control}
              rules={{
                required: { value: true, message: 'This field is required.' },
              }}
              render={({ field }) => (
                <Form.Item
                  help={errors.value?.message}
                  validateStatus={errors.value?.message ? 'error' : undefined}>
                  <TextArea rows={4} placeholder="Value" {...field} />
                </Form.Item>
              )}
            />
          </div>
          <div className="flex flex-1 flex-row justify-end gap-2">
            <Button onClick={() => setUpdateData(undefined)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LabResultVerbiages;
